/**
 * StoryboardBumpersRow — compact intro/outro upload chip row shown
 * inside the storyboard's Final Video panel, directly above the
 * Stitch button.
 *
 * Behaviour:
 *  - Each slot (intro, outro) either shows an upload button OR a
 *    thumbnail + duration + ✕ remove control.
 *  - When the storyboard has no per-video bumper for a kind but the
 *    parent channel does, we show an "(inherited)" hint referencing
 *    the channel default so the user knows why the stitch is going
 *    to include a clip they didn't upload on this page.
 *  - Uploads/deletes call the storyboard-scoped endpoints; the
 *    channel-scoped ones remain unchanged.
 *
 * Props:
 *   storyboardId → id
 *   compact      → optional: hides help text, uses smaller chips
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  listStoryboardBumpers,
  uploadStoryboardBumper,
  deleteStoryboardBumper,
  channelBumperPreviewUrl,
} from "@/lib/api";

export default function StoryboardBumpersRow({ storyboardId }) {
  const [bumpers, setBumpers] = useState({ intro: null, outro: null });
  const [channelDefaults, setChannelDefaults] = useState({ intro: null, outro: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listStoryboardBumpers(storyboardId);
      setBumpers(r?.bumpers || { intro: null, outro: null });
      setChannelDefaults(r?.channel_defaults || { intro: null, outro: null });
    } catch {
      // Silent — the user still gets to hit Stitch either way.
    } finally {
      setLoading(false);
    }
  }, [storyboardId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      data-testid="storyboard-bumpers-row"
      className="flex items-center gap-3 flex-wrap text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 border-t border-zinc-900 pt-3"
    >
      <span>Bumpers:</span>
      <BumperChip
        kind="intro"
        storyboardId={storyboardId}
        bumper={bumpers.intro}
        inherited={channelDefaults.intro}
        disabled={loading}
        onChanged={load}
      />
      <BumperChip
        kind="outro"
        storyboardId={storyboardId}
        bumper={bumpers.outro}
        inherited={channelDefaults.outro}
        disabled={loading}
        onChanged={load}
      />
      <span className="text-zinc-700 normal-case tracking-normal">
        mp4 · ≤30s · stitched at video ends
      </span>
    </div>
  );
}

function BumperChip({ kind, storyboardId, bumper, inherited, disabled, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = () => {
    if (busy || disabled) return;
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.mp4$|video\/mp4/i.test(file.name + " " + file.type)) {
      toast.error("Only .mp4 files are accepted");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File is over 50 MB");
      return;
    }
    setBusy(true);
    try {
      await uploadStoryboardBumper(storyboardId, kind, file);
      toast.success(`${kind === "intro" ? "Intro" : "Outro"} attached`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to upload ${kind}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    if (!window.confirm(`Remove ${label.toLowerCase()} clip from this video?`)) return;
    setBusy(true);
    try {
      await deleteStoryboardBumper(storyboardId, kind);
      toast.success(`${kind === "intro" ? "Intro" : "Outro"} removed`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to remove ${kind}`);
    } finally {
      setBusy(false);
    }
  };

  const label = kind === "intro" ? "Intro" : "Outro";
  const testKind = kind;

  if (bumper) {
    // Attached at storyboard scope — show a filled chip with size + remove.
    const previewUrl = channelBumperPreviewUrl(bumper.preview_url);
    return (
      <span
        data-testid={`sb-bumper-chip-${testKind}`}
        className="inline-flex items-center gap-2 border border-emerald-500/50 bg-emerald-500/10 text-emerald-200 px-2 py-1 rounded-sm normal-case tracking-normal"
      >
        <a
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
          title="Preview clip"
        >
          {label} · {bumper.duration_sec ? `${bumper.duration_sec.toFixed(1)}s` : `${(bumper.size_bytes / (1024*1024)).toFixed(1)} MB`}
        </a>
        <button
          type="button"
          onClick={remove}
          disabled={busy}
          data-testid={`sb-bumper-remove-${testKind}`}
          className="hover:text-red-300 disabled:opacity-40"
          title={`Remove ${label.toLowerCase()}`}
        >
          ✕
        </button>
      </span>
    );
  }

  if (inherited) {
    // Nothing per-video, but channel has one — show inherited badge and
    // an "override" button.
    return (
      <span
        data-testid={`sb-bumper-inherited-${testKind}`}
        className="inline-flex items-center gap-2 border border-zinc-700 bg-zinc-900 text-zinc-300 px-2 py-1 rounded-sm normal-case tracking-normal"
      >
        <span title="From channel default">{label} · from channel</span>
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          data-testid={`sb-bumper-override-${testKind}`}
          className="hover:text-yellow-300 disabled:opacity-40 font-mono text-[10px] uppercase tracking-widest"
          title={`Override with a different ${label.toLowerCase()}`}
        >
          override
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4"
          className="hidden"
          onChange={onFile}
          data-testid={`sb-bumper-input-${testKind}`}
        />
      </span>
    );
  }

  // Empty — show the upload chip.
  return (
    <>
      <button
        type="button"
        onClick={pick}
        disabled={busy || disabled}
        data-testid={`sb-bumper-upload-${testKind}`}
        className="inline-flex items-center gap-1.5 border border-dashed border-zinc-700 hover:border-yellow-400/60 hover:text-yellow-300 text-zinc-400 px-2 py-1 rounded-sm normal-case tracking-normal disabled:opacity-40"
      >
        {busy ? "…" : `+ ${label}`}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4"
        className="hidden"
        onChange={onFile}
        data-testid={`sb-bumper-input-${testKind}`}
      />
    </>
  );
}
